import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {displayLocalValidationError} from "../../../helpers/forms/validationErrors/utils";
import {clearFeedbacksByName, fillInputsByName} from "../../../helpers/forms";
import {getSingleInputByNameV3} from "../../../helpers/forms/formInputsHelpers";
import {TotalPreviewComponent} from "../TotalPreviewComponent/TotalPreviewComponent";

class PaymentInfoStage extends BaseStage {

    /** @var {TotalPreviewComponent} */
    totalPreviewComponent;

    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Get route contents
        const getViewHtmlRoute = this.domContainer.dataset.getPayementInfoViewRoute;

        this.fields = [
            'first_name',
            'last_name',
            'phone',
            'email',
            'address1',
            'address2',
            'city',
            'state',
            'zip',
            'cc_number',
            'cc_month',
            'cc_year',
            'cc_cvv',
        ]

        this.requiredFields = [
            'first_name',
            'last_name',
            'phone',
            'email',
            'address1',
            'city',
            'state',
            'zip',
            'cc_number',
            'cc_month',
            'cc_year',
            'cc_cvv',
        ];

        loading();

        Axios.post(
            getViewHtmlRoute,
            {
                order_id: this.globalState.orderId,
            }
        )
            .then(response => {
                // Retrieve HTML from server
                const viewHtml = createElement(response.data.view);

                // Replace contents from server
                this.domContainer.innerHTML = viewHtml.innerHTML;

                // Run default components initializations
                this.initSharedComponents();

                this.initView();
            })
            .catch(error => {
                handleAjaxErrorV2(error)

                this.globalState.stageManager.previousStage();
                this.globalState.stageManager.clearStageInstance('payment-info');
            })
            .finally(stopLoading);

    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'payment-info';
    }

    initView() {
        // Init field event handlers
        this.fields.forEach(field => {
            const input = this.domContainer.querySelector(`input[name="${field}"],select[name="${field}"]`);
            if (input) {
                input.addEventListener('change', () => {
                    this.validateField(input);
                    this.updatePaymentInfo();
                });
                input.addEventListener('keyup', () => {
                    this.validateField(input);
                    this.updatePaymentInfo();
                });
                input.addEventListener('blur', () => {
                    this.validateField(input);
                })
            }

        })

        // Fill in user payment info if already done
        if (this.globalState.paymentInfo) {
            fillInputsByName(this.domContainer, this.globalState.paymentInfo);

            // Update payment info, to validate if we can continue
            // to the next stage
            this.updatePaymentInfo();
        }

        this.totalPreviewComponent = this.getTotalPreviewComponent()

        // Override default next stage button
        this.domContainer.querySelector('.next-stage-button')
            .addEventListener(
                'click', e => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (this.allRequiredFieldsFilled()) {
                        this.globalState.stageManager.nextStage();
                    } else {
                        displayLocalValidationError(this.domContainer, '_', 'Please fill in all required fields')
                    }
                },
                {
                    capture: true,
                }
            )

        this.updateTotals()
            .then(stopLoading);
    }

    getTotalPreviewComponent() {
        return new TotalPreviewComponent(
            this.domContainer.querySelector('.totals-preview-wrapper'),
            this.globalState
        );
    }

    /**
     * @param {HTMLInputElement} fieldElement
     */
    validateField(fieldElement) {
        const fieldContainer = fieldElement.parentElement.parentElement
        const value = fieldElement.value.trim();

        clearFeedbacksByName(fieldContainer, [fieldElement.name]);

        const fieldRequired = this.requiredFields.includes(fieldElement.name);

        if (fieldRequired && value === '') {
            displayLocalValidationError(
                fieldContainer.parentElement,
                fieldElement.name,
                `${this.getFieldDisplayName(fieldElement.name)} is required`
            )
        }
    }

    /**
     * Save payment info to state
     */
    updatePaymentInfo() {
        const paymentInfo = {};

        this.fields.forEach(field => {
            paymentInfo[field] = getSingleInputByNameV3(this.domContainer, field).trim();
        })

        this.globalState.setPaymentInfo(paymentInfo);

        this.enableNextStageButton()
    }

    allRequiredFieldsFilled() {
        let allFilled = true;

        Object.keys(this.globalState.paymentInfo).forEach(fieldName => {
            const value = this.globalState.paymentInfo[fieldName];

            if (this.requiredFields.includes(fieldName)) {
                if (!value) {
                    allFilled = false;
                }
            }
        })
        return allFilled;
    }

    getFieldDisplayName(field) {
        const fieldDisplayNameMap = {
            first_name: 'First Name',
            last_name: 'Last Name',
            phone: 'Phone',
            email: 'Email',
            address1: 'Address',
            address2: 'Address',
            city: 'City',
            state: 'State',
            zip: 'Zip',
            cc_number: 'Credit card number',
            cc_month: 'Credit card month',
            cc_year: 'Credit card year',
            cc_cvv: 'Credit card cvv',

        };

        return fieldDisplayNameMap[field];
    }

    updateTotals() {
        return this.totalPreviewComponent.updateTotalsFromServer();
    }
}

export {PaymentInfoStage};
